import { FC, useContext } from 'react';
import {
  Group,
  Text,
  Box,
  Button,
  Title,
  Table,
  Paper,
  Skeleton,
  Loader,
  Flex,
} from '@mantine/core';
import Badge from 'components/Badge';
import { IconDownload } from '@tabler/icons-react';
import { useDeviceType } from 'hooks/useDeviceType';

import classes from './index.module.scss';
import { AccessLevel } from 'interfaces/GetUserResult';
import AuthContext from 'context/AuthContext';
import UpgradeOverlay from './UpgradeOverlay';
import { ICallHistoryTableData } from '.';

interface CallHistoryTableDesktopViewProps {
  data: ICallHistoryTableData[];
  onViewDetails: (id: string) => void;
  onExport: () => void;
  isLoading?: boolean;
  isDownloading?: boolean;
}

const CallHistoryTableDesktopView: FC<CallHistoryTableDesktopViewProps> = ({
  data,
  onViewDetails,
  onExport,
  isLoading,
  isDownloading,
}) => {
  const { isSmallDesktop } = useDeviceType();
  const {
    data: { account },
  } = useContext(AuthContext);
  const isPremium = account?.access_level === AccessLevel.PRO;

  return (
    <Paper radius="md" pos="sticky" top="0">
      <Group
        justify="space-between"
        mb={-1}
        py="0.7rem"
        px="md"
        bg="white"
        pos={isPremium && data.length > 0 ? 'sticky' : 'relative'}
        top="0"
        bd="1px solid var(--border-color)"
        style={{
          zIndex: 10,
          borderTopLeftRadius: 'var(--mantine-radius-md)',
          borderTopRightRadius: 'var(--mantine-radius-md)',
        }}
      >
        <Title size="1rem" fw={700}>
          Call detail history
        </Title>
        <Button
          variant="transparent"
          size="sm"
          fw={400}
          p={0}
          w={{ lg: '11.2rem' }}
          disabled={
            isDownloading || isLoading || data.length === 0 || !isPremium
          }
          onClick={onExport}
          style={{ opacity: isDownloading ? 0.6 : 1 }}
          bg="none"
          rightSection={
            isDownloading ? (
              <Loader size="0.75rem" style={{ marginLeft: '0.7rem' }} />
            ) : (
              <IconDownload
                size={19}
                stroke={1.5}
                style={{ marginLeft: '0.25rem', marginBottom: '0.2rem' }}
              />
            )
          }
        >
          Export call history
        </Button>
      </Group>
      {data.length === 0 && !isLoading ? (
        <Group
          py="0.7rem"
          px="md"
          bd="1px solid var(--border-color)"
          style={{
            borderBottomLeftRadius: 'var(--mantine-radius-md)',
            borderBottomRightRadius: 'var(--mantine-radius-md)',
          }}
        >
          <Text c="dimmed" fz="sm">
            No call data available for the selected date range
          </Text>
        </Group>
      ) : (
        <UpgradeOverlay
          style={{
            border: '1px solid var(--border-color)',
            borderTop: 'none',
            borderBottomLeftRadius: 'var(--mantine-radius-md)',
            borderBottomRightRadius: 'var(--mantine-radius-md)',
          }}
        >
          <Table
            striped="even"
            stripedColor="var(--bg-color)"
            horizontalSpacing="md"
            verticalSpacing="0.6rem"
            stickyHeader={isPremium}
            stickyHeaderOffset="3.6rem"
          >
            <Table.Thead
              style={{
                boxShadow: '0 1px 1px var(--border-color)',
              }}
            >
              <Table.Tr bd={0} bg="var(--bg-color)">
                <Table.Th fw={400}>
                  {isSmallDesktop ? 'Number' : 'Calling number'}
                </Table.Th>
                <Table.Th fw={400}>
                  {isSmallDesktop ? 'Call start' : 'Call start date/time'}
                </Table.Th>
                <Table.Th fw={400}>
                  {isSmallDesktop ? 'Duration' : 'Call duration'}
                </Table.Th>
                <Table.Th fw={400}>
                  {isSmallDesktop ? 'Status' : 'Call status'}
                </Table.Th>
                <Table.Th fw={400}>Sentiment</Table.Th>
                <Table.Th fw={400} w={{ xl: '12rem' }}>
                  Actions
                </Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {data.map((record) => {
                return (
                  <Table.Tr
                    key={record.id}
                    className={classes.tableRow}
                    style={{
                      cursor: record.isIncomplete ? 'not-allowed' : 'pointer',
                      pointerEvents: record.isIncomplete ? 'none' : 'auto',
                    }}
                    onClick={() =>
                      record.isIncomplete ? null : onViewDetails(record.id)
                    }
                  >
                    <Table.Td fw={500}>{record.phoneNumber}</Table.Td>
                    <Table.Td>{record.startTime}</Table.Td>
                    <Table.Td>
                      {record.isIncomplete ? (
                        <Flex align="baseline" gap="xs">
                          <Text c="dimmed" fz="0.875rem">
                            Ongoing
                          </Text>
                          <Loader
                            size="0.75rem"
                            type="dots"
                            color="var(--mantine-color-gray-6)"
                          />
                        </Flex>
                      ) : (
                        record.duration
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Badge color={record.statusColor} variant="light">
                        {record.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Group gap="xs">
                        {record.sentimentSmiley}
                        {record.sentimentPercentage && (
                          <Text fw={700} ml="xs" className={classes.value}>
                            {record.sentimentPercentage}
                          </Text>
                        )}
                        {!isSmallDesktop && (
                          <Text
                            c="dimmed"
                            className={classes.value}
                            ml={record.sentimentPercentage ? 0 : 'xs'}
                          >
                            {record.sentiment}
                          </Text>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Button
                        variant="default"
                        fw={400}
                        size="sm"
                        className={classes.value}
                        disabled={record.isIncomplete}
                        onClick={() =>
                          record.isIncomplete ? null : onViewDetails(record.id)
                        }
                      >
                        View details
                      </Button>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
              {isLoading && (
                <Table.Tr className={classes.tableRow}>
                  <Table.Td>
                    <Skeleton h={22} w="7.2rem" />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={22} w="7.5rem" />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={22} w="3.4rem" />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={22} w="4.2rem" />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={22} w="7rem" />
                  </Table.Td>
                  <Table.Td>
                    <Skeleton h={36} w="7.1rem" />
                  </Table.Td>
                </Table.Tr>
              )}
            </Table.Tbody>
          </Table>
          {/*to take care of border radius issue*/}
          <Box h={5} />
        </UpgradeOverlay>
      )}
    </Paper>
  );
};

export default CallHistoryTableDesktopView;
